import { useGetAIUtrvFurtherNotesDraftMutation, useGetAIUtrvFurtherNotesDraftRefineMutation } from '@api/ai';
import { CopyButton } from '@features/copy';
import { SurveyModelMinimalUtrv } from '@g17eco/types/survey';
import { useEffect, useMemo } from 'react';
import { QueryError } from '@g17eco/molecules/query/QueryError';
import { SectionLoader } from '@g17eco/atoms/loader';
import { AIRefineButton, HandleRefineData } from './AIRefineButton';
import { AI_MESSAGE } from '@constants/ai';
import type { InitiativeUniversalTracker } from '@g17eco/types/initiativeUniversalTracker';
import { isExcludedFromAiProcessing } from '@utils/initiative-universal-tracker';

interface Props {
  utrCode: string;
  utrv: SurveyModelMinimalUtrv;
  initiativeUtr: Pick<InitiativeUniversalTracker, 'aiProcessing'> | undefined;
}
export const FurtherNotes = ({ utrCode, utrv, initiativeUtr }: Props) => {
  const [
    getFurtherNotesDraft,
    { isLoading: isGeneratingDraft, data: draftData, isError: isDraftError, error: draftError },
  ] = useGetAIUtrvFurtherNotesDraftMutation();
  const [
    getFurtherNotesDraftRefine,
    { isLoading: isRefining, data: refineData, isError: isRefineError, error: refineError },
  ] = useGetAIUtrvFurtherNotesDraftRefineMutation();

  const isExcludedFromAi = isExcludedFromAiProcessing(initiativeUtr);

  const suggestedFurtherNotes = useMemo(() => {
    if (isExcludedFromAi) {
      return AI_MESSAGE.EXCLUDED_METRIC_SINGULAR;
    }
    return refineData?.content ?? draftData?.content ?? '';
  }, [draftData, refineData, isExcludedFromAi]);

  useEffect(() => {
    // Skip API call if metric is excluded from AI processing
    if (!isExcludedFromAi) {
      getFurtherNotesDraft({ initiativeId: utrv.initiativeId, utrvId: utrv._id, draftData: utrv });
    }
  }, [getFurtherNotesDraft, utrv, isExcludedFromAi]);

  const getError = () => {
    const error = isDraftError ? draftError : isRefineError ? refineError : null;
    return error ? <QueryError error={error} type={'danger'} /> : null;
  };

  return (
    <div>
      <div className='d-flex justify-content-between align-items-center'>
        <h6 className='my-0 text-ThemeHeadingDark'>AI Draft Further Explanation/Notes:</h6>
        {!isExcludedFromAi && (
          <div className='flex-grow-0 d-flex gap-1 align-items-center'>
            <CopyButton content={suggestedFurtherNotes} />
            <AIRefineButton
              handleRefine={(data: HandleRefineData) => {
                getFurtherNotesDraftRefine({
                  initiativeId: utrv.initiativeId,
                  utrvId: utrv._id,
                  draftData: utrv,
                  action: data.action,
                  additionalContext: data.additionalContext,
                  textToRefine: suggestedFurtherNotes,
                });
              }}
              utrCode={utrCode}
              textContent={suggestedFurtherNotes}
            />
          </div>
        )}
      </div>
      {getError()}
      {!isExcludedFromAi && (isGeneratingDraft || isRefining) ? (
        <SectionLoader />
      ) : (
        <p className='text-ThemeTextMedium'>{suggestedFurtherNotes}</p>
      )}
    </div>
  );
};
